import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Anchor, CornerDownRight, CheckCircle2, Clock, Send } from "lucide-react";

// Looping mini-mockup of the tester-facing public status page (/s/:token).
const STEPS = [
  { status: "new", label: "New", badge: "bg-orange text-white",
    events: [{ id: "e0", kind: "sys", text: "You submitted this" }] },
  { status: "in_progress", label: "In progress", badge: "bg-sunny text-ink",
    events: [
      { id: "e0", kind: "sys", text: "You submitted this" },
      { id: "e1", kind: "team", text: "Thanks for the report! Which browser are you on?" },
    ] },
  { status: "in_progress", label: "In progress", badge: "bg-sunny text-ink",
    events: [
      { id: "e0", kind: "sys", text: "You submitted this" },
      { id: "e1", kind: "team", text: "Thanks for the report! Which browser are you on?" },
      { id: "e2", kind: "you", text: "Chrome 141 on Windows 11." },
    ] },
  { status: "resolved", label: "Resolved", badge: "bg-grass text-white",
    events: [
      { id: "e0", kind: "sys", text: "You submitted this" },
      { id: "e1", kind: "team", text: "Thanks for the report! Which browser are you on?" },
      { id: "e2", kind: "you", text: "Chrome 141 on Windows 11." },
      { id: "e3", kind: "status", text: "Status updated to Resolved" },
      { id: "e4", kind: "team", text: "Fixed and shipped in v1.4.2 — thanks for the help! 🎉" },
    ] },
];

export const PublicStatusDemo = () => {
  const [step, setStep] = useState(0);
  useEffect(() => {
    const delays = [1900, 2100, 2100, 3400];
    const t = setTimeout(() => setStep((s) => (s + 1) % STEPS.length), delays[step]);
    return () => clearTimeout(t);
  }, [step]);

  const cur = STEPS[step];

  return (
    <div className="nb-card bg-white p-5 sm:p-6 rotate-1 max-w-md mx-auto w-full" data-testid="public-status-demo" aria-hidden="true">
      <div className="flex items-center gap-2 mb-4">
        <Anchor className="h-4 w-4 text-orange" />
        <span className="font-display font-800 text-sm">Tugboat</span>
        <span className="text-ink/40 font-700 text-xs">· Acme Bank</span>
      </div>

      <div className="flex items-center justify-between gap-3 mb-4">
        <div>
          <p className="text-[10px] font-800 uppercase tracking-wide text-ink/45">Your submission</p>
          <h3 className="font-display text-2xl font-800 leading-none">#42</h3>
        </div>
        <motion.span key={cur.label} initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
          transition={{ type: "spring", stiffness: 300, damping: 18 }}
          className={`text-[10px] font-800 uppercase px-3 py-1.5 rounded-full border-2 border-ink ${cur.badge}`}>
          {cur.label}
        </motion.span>
      </div>

      <p className="text-sm bg-sand border-2 border-ink p-3 rounded-2xl font-600 mb-4">Transfer confirmation spins forever after I click Send.</p>

      <div className="space-y-2.5 min-h-[188px]">
        <AnimatePresence initial={false}>
          {cur.events.map((e) => (
            <motion.div key={e.id} layout initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
              transition={{ type: "spring", stiffness: 260, damping: 22 }}>
              {e.kind === "sys" && (
                <p className="flex items-center gap-2 text-xs font-700 text-ink/50">
                  <span className="h-2 w-2 rounded-full bg-ink/30" /> {e.text}
                </p>
              )}
              {e.kind === "status" && (
                <p className="flex items-center gap-2 text-xs font-700 text-ink/70">
                  <CheckCircle2 className="h-3.5 w-3.5 text-grass shrink-0" /> {e.text}
                </p>
              )}
              {e.kind === "team" && (
                <div className="border-2 border-ink rounded-2xl p-3 bg-teal/25">
                  <div className="flex items-center gap-1.5 text-xs font-800"><CornerDownRight className="h-3 w-3" /> Team
                    <span className="ml-auto text-ink/45 font-600 flex items-center gap-1"><Clock className="h-3 w-3" />just now</span></div>
                  <p className="text-sm font-600 mt-1">{e.text}</p>
                </div>
              )}
              {e.kind === "you" && (
                <div className="border-2 border-ink rounded-2xl p-3 bg-sand">
                  <div className="flex items-center gap-1.5 text-xs font-800"><CornerDownRight className="h-3 w-3" /> You</div>
                  <p className="text-sm font-600 mt-1">{e.text}</p>
                </div>
              )}
            </motion.div>
          ))}
        </AnimatePresence>
      </div>

      <div className="flex items-center gap-2 mt-4 border-2 border-ink/20 rounded-xl px-3 py-2">
        <span className="text-sm text-ink/40 font-600 flex-1">Reply to the team…</span>
        <span className="nb-btn bg-orange text-white text-xs px-3 py-1.5"><Send className="h-3 w-3" /></span>
      </div>
    </div>
  );
};
